import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Heading, IconButton } from '../../../components/base';
import { Button as Icon } from '../../../components/Button';
import { useAlert } from '../../../context/AlertContext';
import { usePlugin } from '../../../libs/hooks/usePlugin';
import {
  AuthorityType,
  ECreatedStatusType,
  EOpenScopeType,
  EVersionStatusType,
  IPluginPromptData,
} from '../../../libs/models/studio/PluginPromptList';
import { useAppDispatch, useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import PluginCreateForm from './PluginCreateForm';
import PluginCreatePreview from './PluginCreatePreview';
import { showAlertToast } from '../../../redux/features/app/appSlice';
import { AlertType } from '../../../libs/models/AlertType';
import { Constants } from '../../../Constants';

const LoadingSpinner = () => {
  return (
    <div className="dsx-ModalOverlay">
      <div className="works-loading">
        <span className="loading" aria-hidden="true"></span>
      </div>
    </div>
  );
};

// Create > CreateForm > 여기서 step별로 나뉘어짐
const PluginCreate = () => {
  const plugin = usePlugin();
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const [query] = useSearchParams();
  const { user } = useAppSelector((state: RootState) => state.users);
  const dispatch = useAppDispatch();
  const [isRegistLoading, setIsRegistLoading] = useState(false);
  const DEFULT_IMAGE = `${process.env.REACT_APP_STORAGE_IMAGE_URL}/image/studio/emoji01.png`;

  const [formData, setFormData] = useState<IPluginPromptData>({
    partitionKey: Constants.companyCode,
    deleted: false,
    companyCode: Constants.companyCode,
    pluginCategoryId: '',
    name: '',
    description: '',
    model: '',
    profileUrl: DEFULT_IMAGE,
    openScope: EOpenScopeType.Private,
    customPersona: '',
    ragFileIds: [],
    ragContentsData: { id: '', contentsDataFileName: '', uploadStatus: 'NotStarted' },
    ragContentsDataId: '',
    conversationStarters: [],
    bookmarkUserIds: [],
    enabled: true,
    createdMember: user,
    createdStep: 1,
    versionStatus: EVersionStatusType.Draft, // 임시저장
    createdStatus: ECreatedStatusType.Registration, // 등록중
    managedMembers: [{ authorityType: AuthorityType.Owner, user: user }],
    setting: {shouldResponseFromSearch: false, allowUseWebSearch: false, agentKnowCurrentTime: true},
    kmsRules: [],
  });

  useEffect(() => {
    const id = query.get('id');

    if (id) {
      void handleCallDetail(id);
    } else {
      // 2502 초기 진입시에 임시저장 x > 이미지 생성 dialog 클릭시 생성으로 변경
      // handleSave('temp');
    }
  }, []);
  /**
   * [Func] 상세 조회
   */
  const handleCallDetail = async (id: string) => {
    try {
      const res = await plugin.getPlugin(id);
      setFormData({ ...res });
    } catch (error) {
      console.log(error);
      showAlert({
        title: '오류',
        message: (
          '수정 권한이 없습니다.'
        ),
        confirmLabel: '확인',
        showCancel: false,
        onConfirm: () =>{navigate('/makers/agent')},
      });
    }
  };

  /**
   * [Func] 임시저장, 저장
   * @param type
   */
  const handleCallSave = async (type: string, isShowAlert?: boolean) => {
    if (type === 'temp') {
      // 임시 저장
      try {
        if (formData.id) {
          const res = await plugin.postPluginDraft({
            ...formData,
            conversationStarters: formData.conversationStarters?.filter((starter) => starter !== ''),
            createdStatus: formData.createdStatus === 'Complete' ? 'Registration' : formData.createdStatus,
          });
          if (res.statusCode === 200) {
            setFormData((prev) => ({ ...prev, id: res.value.id }));
            isShowAlert &&
              dispatch(
                showAlertToast({
                  altMessage: '임시저장 되었습니다.',
                  altType: AlertType.Info,
                }),
              );
          } else {
            dispatch(
              showAlertToast({
                altMessage: String(res.value.message),
                altType: AlertType.Info,
              }),
            );
          }
        } else {
          const res = await plugin.postPluginDraft({
            ...formData,
            conversationStarters: formData.conversationStarters?.filter((starter) => starter !== ''),
          });
          if (res.statusCode === 200) {
            setFormData((prev) => ({
              ...prev,
              id: res.value.id,
            }));
          } else {
            dispatch(
              showAlertToast({
                altMessage: String(res.value.message),
                altType: AlertType.Info,
              }),
            );
          }
        }
      } catch (error) {
        console.log(error);
      }
    } else {
      // 배포하기
      showAlert({
        title: '에이전트를 등록하시겠어요?',
        message: (
          <div className="alert-box">
            <strong className="tit">공개 범위</strong>{' '}
            <span className="con">{formData.openScope === EOpenScopeType.Public ? '전체공개' : formData.openScope === EOpenScopeType.Part ? '일부공개': '나만보기'}</span>
          </div>
        ),
        confirmLabel: '배포하기',
        showCancel: true,
        onConfirm: () => void handleRegist(),
      });
    }
  };

  /**
   * 배포하기 (등록)
   */
  const handleRegist = async () => {
    setIsRegistLoading(true);
    try {
      // CASE.1 임시저장인 상태에서, id 값이 있는경우
      if ((formData.versionStatus === 'Draft' || formData.versionStatus === EVersionStatusType.Draft) && formData.id) {
        // 임시저장된 상태에서 아무것도 변경되지 않고 상태만 등록으로 변경
        await plugin.postPlugin({
          ...formData,
          conversationStarters: formData.conversationStarters?.filter((starter) => starter !== ''),
          createdStep: 1, // 등록시 step1 으로 저장
          versionStatus: EVersionStatusType.Creat, // 최초
          createdStatus: ECreatedStatusType.Complete,
        });
        setFormData((prev) => ({ ...prev, id: formData.id }));
        await plugin.postPluginBookmark(formData.id);
        setIsRegistLoading(false);
        navigate('/makers/agent');
        // CASE.2 최초 등록 상태에서, id 값이 있는경우 or CASE.3 업데이트 상태에서, id 값이 있는경우
      } else if (
        (formData.versionStatus === 'Creat' ||
          formData.versionStatus === EVersionStatusType.Creat ||
          formData.versionStatus === 'Update' ||
          formData.versionStatus === EVersionStatusType.Update) &&
        formData.id
      ) {
        await plugin.postPlugin({
          ...formData,
          conversationStarters: formData.conversationStarters?.filter((starter) => starter !== ''),
          createdStep: 1, // 등록시 step1 으로 저장
          versionStatus: EVersionStatusType.Update, // 업데이트버전
          createdStatus: ECreatedStatusType.Complete,
        });
        setFormData((prev) => ({ ...prev, id: formData.id }));
        setIsRegistLoading(false);
        navigate('/makers/agent');
      }
    } catch (error) {
      console.log(error);
    }
    setIsRegistLoading(false);
  };

  /**
   * [Func] 임시저장, 저장
   * @param type
   */
  const handleSave = (type: string, isShowAlert?: boolean) => {
    void handleCallSave(type, isShowAlert);
  };

  const handleBack = () => {
    showAlert({
      title: '저장하지 않고 페이지를 나가시겠습니까?',
      message: '변경사항이 저장되지 않을 수 있습니다.',
      confirmLabel: '나가기',
      showCancel: true,
      onConfirm: () => {
        navigate('/makers/agent');
      },
    });
  };

  const handleSubmit = () => {
    showAlert({
      title: '에이전트를 등록하시겠습니까?',
      message: (
        <div className="alert-box">
          <strong className="tit">공개 범위</strong> <span className="con">전체공개</span>
        </div>
      ),
      confirmLabel: '배포하기',
      showCancel: true,
      onConfirm: () => {
        handleSave('save');
      },
    });
  };

  const handleDelete = () => {
    showAlert({
      title: '에이전트를 삭제하시겠습니까?',
      message:
        // <div className="alert-box">
        formData.versionStatus === 'Draft' || formData.versionStatus === EVersionStatusType.Draft
          ? '임시저장된 데이터는 복구 할 수 없습니다.'
          : '삭제 전에 다시 한번 확인해주세요.',
      // {/* </div> */}
      confirmLabel: '삭제하기',
      showCancel: true,
      onConfirm: () => {
        setIsRegistLoading(true);
        plugin
          .deletePlugin(formData)
          .then(() => {
            setIsRegistLoading(false);
            navigate('/makers/agent');
          })
          .catch(() => {
            setIsRegistLoading(false);
          });
      },
    });
  };

  return (
    <div className="create-wrapper">
      {isRegistLoading && <LoadingSpinner />}
      <div className="create-header">
        <IconButton name="chevronLeft" size="large" onClick={handleBack}>
          뒤로가기
        </IconButton>
        <Heading as="h1" size="title3" weight="semibold">
          에이전트 생성
        </Heading>
        <Icon
          className="create-delete"
          control
          icon="delete"
          textBlind={true}
          onClick={() => {
            handleDelete();
          }}
        >
          삭제
        </Icon>
        <div className="h-side">
          <Button
            variant="secondary"
            size="large"
            onClick={() => {
              handleSave('temp', true);
            }}
          >
            임시저장
          </Button>
          <Button
            variant="primary"
            size="large"
            onClick={() => {
              handleSave('save');
            }}
            disabled={!(formData.createdStep === 3)}
          >
            배포하기
          </Button>
        </div>
      </div>

      <div className="create-container">
        <div className="create-form">
          <PluginCreateForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            handleSave={handleSave}
          />
        </div>
        <div className="create-test">
          <PluginCreatePreview
            formData={formData}
            handleSave={(type, isShowAlert) => handleCallSave(type, isShowAlert)}
          />
        </div>
      </div>
    </div>
  );
};

export default PluginCreate;
